import React, { useState, useMemo } from 'react'
import { Stack, Group, Title, TextInput } from '@mantine/core'
import { debounce } from 'lodash'
import { IconSearch } from '@tabler/icons-react'
import Table from '../../../components/AdminDataTable'
import { router } from '@inertiajs/react'
import { AdminBrandsIndexProps } from './types'
import { columns } from './columns'
import type { SortParams } from '../../../components/AdminDataTable'
import type { Brand } from '../../../types'

const AdminBrandsIndex: React.FC<AdminBrandsIndexProps> = (props) => {
  const { brands, pagination, sort, search } = props

  const [searchValue, setSearchValue] = useState(search)

  const searchQuery = (value: string) => value ? { title_cont: value } : undefined

  const debouncedSearch = useMemo(
    () => debounce((value: string) => {
      router.get(
        '/admin/brands',
        { q: searchQuery(value), page: 1, per_page: pagination.per_page },
        { preserveState: true, preserveScroll: true }
      )
    }, 300),
    []
  )

  const handleSearch = (value: string) => {
    setSearchValue(value)
    debouncedSearch(value)
  }

  const onPageChange = (page: number) =>
    router.get('/admin/brands', {
      q: searchQuery(searchValue),
      page,
      per_page: pagination.per_page,
      sort: sort.key,
      order: sort.order,
    })

  const onSortChange = (s: SortParams) => {
    const base = { q: searchQuery(searchValue), page: 1, per_page: pagination.per_page }
    if (!s.order) {
      router.get('/admin/brands', base)
    } else {
      router.get('/admin/brands', { ...base, sort: s.key, order: s.order })
    }
  }

  const onRowClick = ({ record }: { record: Brand }) =>
    router.visit(`/admin/brands/${record.slug}`)

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Title order={2}>Marques</Title>
        <TextInput
          placeholder="Rechercher une marque…"
          leftSection={<IconSearch size={16} />}
          value={searchValue}
          onChange={(e) => handleSearch(e.currentTarget.value)}
          w={260}
        />
      </Group>
      <Table
        columns={columns}
        data={brands}
        rowKey="slug"
        onRowClick={onRowClick}
        sortStatus={sort.order ? { columnAccessor: sort.key, direction: sort.order } : undefined}
        onSortStatusChange={(s) => onSortChange({ key: s.columnAccessor as string, order: s.direction })}
        totalRecords={pagination.total_pages * pagination.per_page}
        recordsPerPage={pagination.per_page}
        page={pagination.current_page}
        onPageChange={onPageChange}
      />
    </Stack>
  )
}

export default AdminBrandsIndex
